/** Shapes of the Notify API (`/api/notify`, web-demo-backend `src/notify/routes.py`).
 * Only the fields the pages use are typed. */

export type NotifyRole = "admin" | "radiologist" | "clinician" | "interventionalist";

export type CaseState = "open" | "alerted" | "acknowledged";

export const CASE_STATES: CaseState[] = ["open", "alerted", "acknowledged"];

/** One of the caller's active sites in `GET /me` (their `people` row there). */
export interface NotifySiteMembership {
  site_id: string;
  name: string | null;
  roles: NotifyRole[];
  /** Tells the caller's own ack and events from someone else's. */
  person_id: string;
  display_name: string | null;
}

export interface NotifyMe {
  sites: NotifySiteMembership[];
}

export interface NotifyCase {
  site_id: string;
  study_uid: string;
  state: CaseState;
  is_test?: boolean;
  flagged_at: string;
  alert_sent_at?: string | null;
  /** "failed" when no push was delivered (the state stays open); older rows
   * have neither field. */
  alert_status?: "failed" | null;
  alert_failed_at?: string | null;
  owner_person_id?: string | null;
  /** Set on ack; rows acknowledged before it existed have none. */
  owner_display_name?: string | null;
  acknowledged_at?: string | null;
}

/** Event types the backend writes in S1. */
export type NotifyEventType =
  | "flagged"
  | "alert_sent"
  | "alert_failed"
  | "alert_skipped"
  | "acknowledged"
  | "received_ack";

/** What an alert did for one recipient: `no_device` = no subscription (alerts
 * off), `not_tried` = not reached before the send stopped. */
export type AlertOutcome = "sent" | "failed" | "no_device" | "not_tried";

export interface AlertRecipient {
  person_id: string;
  display_name: string | null;
  outcome: AlertOutcome;
}

export interface NotifyEvent {
  event_id: string;
  at: string;
  type: NotifyEventType | (string & {});
  actor_person_id?: string;
  data?: {
    recipients?: string[];
    /** On `alert_sent` / `alert_failed`: each recipient's result. Older events have none. */
    people?: AlertRecipient[];
    reason?: string;
  } & Record<string, unknown>;
}

export interface NotifyCaseDetail {
  case: NotifyCase;
  events: NotifyEvent[];
}
